import {
  type QueryFunctionContext,
  type UseQueryResult,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { configStore } from "../../contexts/ConfigContext";
import type {
  LearningSet,
  LearningSetTypes,
} from "../../interfaces/AthenaResources";
import { filterLearningSets } from "../../pages/SoloAIModuleList/utils";
import {
  type LegacyFormat,
  reformatResources,
} from "../../pages/Workmodes/mediaResourceUtils/reformatResources";
import type { Tuto, TutosCollection } from "../../pages/Workmodes/Tuto/type";
import type { MediaResourceItem } from "../../pages/Workmodes/types";
import type {
  Workshop,
  WorkshopsCollection,
} from "../../pages/Workmodes/Workshop/types";
import { filterDefined } from "../../utils/array-manipulations";
import { axiosClient } from "../../utils/axios-client/axios-client";
import * as localStorage from "../../utils/localStorage";
import { PLACEHOLDER_SUBJECT, useActiveSubject } from "../useActiveSubject";
import { useSafeMappingTree } from "../useMappingTree";

/**
 * Resource types that follow the schema
 * {
 *  learning_set_type: "type_collection", learning_sets: [
 *      {
 *          learning_set_type: "type",
 *          learning_items: [{ item }]
 *      },
 *      // ...
 *  ]
 * }
 */
type CollectionTypes = Extract<
  LearningSetTypes,
  LearningSetTypes.Tutorial | LearningSetTypes.Workshop
>;
const isCollectionSet = (set: LearningSet) =>
  set.learning_set_type.endsWith("collection");
type CollectionTypeToCollectionSets<T extends CollectionTypes> =
  T extends LearningSetTypes.Tutorial
    ? Tuto | TutosCollection
    : Workshop | WorkshopsCollection;
type CollectionTypeToSet<T extends CollectionTypes> =
  T extends LearningSetTypes.Tutorial
    ? Tuto & { collectionId: string }
    : Workshop & { collectionId: string };

export const useCollectionsQueries = <T extends CollectionTypes>(
  learningSetType: T,
  collectionsIds: string[],
  opts?: { enabled?: boolean },
): {
  queries: UseQueryResult<
    {
      data: Record<string, CollectionTypeToCollectionSets<T>>;
      collectionId: string;
    },
    unknown
  >[];
  /**
   * Contains the list of currently requested sets, grows with each new
   * request that succeed
   */
  data: CollectionTypeToSet<T>[];
} => {
  const mappingTree = useSafeMappingTree();
  const activeSubject = useActiveSubject();
  const { queries, data } = useAllSubjectsCollectionsQueries(
    learningSetType,
    collectionsIds,
    { enabled: opts?.enabled },
  );

  return {
    queries,
    data:
      mappingTree && activeSubject.id !== PLACEHOLDER_SUBJECT.id
        ? filterLearningSets(data, [activeSubject], mappingTree)
        : data,
  };
};

export const useAllSubjectsCollectionsQueries = <T extends CollectionTypes>(
  learningSetType: T,
  collectionsIds: string[],
  opts?: { enabled?: boolean },
): {
  queries: UseQueryResult<
    {
      data: Record<string, CollectionTypeToCollectionSets<T>>;
      collectionId: string;
    },
    unknown
  >[];
  /**
   * Contains the list of currently requested sets, grows with each new
   * request that succeed
   */
  data: CollectionTypeToSet<T>[];
} => {
  const { getCollection } = useCollectionAPI();
  const queries = useQueries({
    queries: collectionsIds.map((collectionId) => ({
      enabled: opts?.enabled,
      queryKey: queryKeys.getCollection(learningSetType, collectionId),
      queryFn: getCollection<T>,
      staleTime: Infinity,
    })),
  });
  const dataList = collectionsQueryResultToDataList(queries);
  return {
    queries,
    data: dataList,
  };
};

export const useCollectionItemQuery = <T extends CollectionTypes>(
  learningSetType: T,
  itemId?: string,
) => {
  const { getCollectionItem } = useCollectionAPI();
  return useQuery({
    enabled: !!itemId,
    queryKey: queryKeys.getCollectionItem(learningSetType, itemId ?? ""),
    queryFn: getCollectionItem,
    staleTime: Infinity,
  });
};

const queryKeys = {
  all: "collections" as const,
  item: "item" as const,

  getCollection: <T extends CollectionTypes>(
    learningSetType: T,
    collectionId: string,
  ) =>
    [
      queryKeys.all,
      learningSetType,
      collectionId,
      { preview: "deep" },
    ] as const,
  getCollectionItem: <T extends CollectionTypes>(
    learningSetType: T,
    itemId: string,
  ) => [queryKeys.all, queryKeys.item, learningSetType, itemId] as const,
};

const useCollectionAPI = () => {
  const {
    config: { apiUrls },
  } = useContext(configStore);

  const getCollection = useCallback(
    async <T extends CollectionTypes>(
      context: QueryFunctionContext<
        ReturnType<typeof queryKeys.getCollection<T>>
      >,
    ) => {
      const collectionId = context.queryKey[2];
      const params = context.queryKey[3];
      const res = await axiosClient.axios.get<LegacyFormat>(
        `${
          apiUrls.endpoints.resources
        }/learning_sets/${collectionId}?${new URLSearchParams(params)}`,
        {
          headers: headers(),
        },
      );
      // TEMP while endpoint is changed from learning_set_id and learning_item_id to id
      // return res.data;
      return {
        data: reformatResources(res.data) as Record<
          string,
          CollectionTypeToCollectionSets<T>
        >,
        collectionId,
      };
    },
    [apiUrls],
  );

  const getCollectionItem = useCallback(
    async <T extends CollectionTypes>(
      context: QueryFunctionContext<
        ReturnType<typeof queryKeys.getCollectionItem<T>>
      >,
    ) => {
      const item = context.queryKey[3];
      const res = await axiosClient.axios.get<LegacyFormat>(
        `${apiUrls.endpoints.resources}/learning_sets/${item}`,
        {
          headers: headers(),
        },
      );
      // TEMP while endpoint is changed from learning_set_id and learning_item_id to id
      // return res.data;
      return reformatResources(res.data as any as LegacyFormat) as Record<
        string,
        CollectionTypeToSet<T> | MediaResourceItem
      >;
    },
    [apiUrls.endpoints.resources],
  );

  return { getCollection, getCollectionItem };
};

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem<string>(
    localStorage.Key.TOKEN,
  )}`,
  "x-evidenceb-app": "athena",
  "Content-Type": "application/json",
  "x-evb-origin": window.location.origin,
});

const collectionsQueryResultToDataList = <T extends CollectionTypes>(
  queries: UseQueryResult<
    {
      data: Record<string, CollectionTypeToCollectionSets<T>>;
      collectionId: string;
    },
    unknown
  >[],
): CollectionTypeToSet<T>[] => {
  return queries
    .flatMap((query) => {
      if (!query.data) return undefined;
      const collection = Object.values(query.data.data).find((set) =>
        isCollectionSet(set),
      );
      if (!collection) return undefined;
      return collection.learning_sets?.map((id) => ({
        ...(query.data.data[id] as CollectionTypeToSet<T>),
        collectionId: query.data.collectionId,
      }));
    })
    .filter(filterDefined);
};
